/** THE CONSOLE'S TEXT SIZE, ITS PURE RULES (ported 2026-10-12 from the medrec console's console-type-scale lane, Rod's standing rule of
 *  10-11: a change to the medrec console's frame or look comes here too; there Rod 2026-10-12 ~10:20 am PT: "i think i have to zoom in to
 *  125% should we resize the site properly", and 11:0x am PT: "GO WITH C"). A copy of sj-medrecapi packages/web/src/lib/text-size-rules.ts;
 *  only the two storage keys differ (this console's `nl-*` family, beside the theme's `nl-theme`).
 *
 *  Three steps, each one factor over the whole console: Standard (1, today's console to the pixel), Large (1.15) and Larger (1.25). The
 *  factor is the root element's `--ts` custom property (styles/text-size.css), and the build multiplies every px length in the console's
 *  stylesheets by it (`text-size-css.ts`); every width rule moves by the same factor (`scaleQuery`). So a step at a window W px wide lays the
 *  console out as Standard at W / factor, which is exactly what browser zoom does, and every column budget the earlier passes measured
 *  (claims.css: "a 1440 screen at 125% zoom is 1152px") holds at that width.
 *
 *  Nothing here touches the DOM or React, so the stylesheet build step (vite.config.ts) and the unit tests read the same numbers. The inline
 *  script in index.html applies the same first-visit rule before the first paint (a unit test pins that the two agree). */

export type TextSize = 'standard' | 'large' | 'larger';

export const TEXT_SIZES: readonly TextSize[] = ['standard', 'large', 'larger'];

/** Each step's factor. Standard is 1: the stylesheets' `calc(Npx * var(--ts, 1))` is then N px exactly. */
export const TEXT_FACTOR: Readonly<Record<TextSize, number>> = { standard: 1, large: 1.15, larger: 1.25 };

/** The words the control shows, plain (Rod's rule: no system words). */
export const TEXT_SIZE_WORDS: Readonly<Record<TextSize, string>> = { standard: 'Standard', large: 'Large', larger: 'Larger' };

/** The local storage key of a person's own pick (the `nl-*` family the theme uses: `nl-theme`). */
export const TEXT_SIZE_KEY = 'nl-text-size';
/** The local storage key that says the one-line hint was seen (shown once per browser). */
export const TEXT_SIZE_HINT_KEY = 'nl-text-size-hint';
/** The root element's attribute the factor keys on (`styles/text-size.css`); absent reads as Standard. */
export const TEXT_SIZE_ATTRIBUTE = 'data-text-size';

/** THE FIRST-VISIT DEFAULT (Rod's pick C, its default rule): the biggest step that still lays the console out at least as wide as today at
 *  1,440 px, the width the earlier passes tuned most: Larger from 1,800 px windows (1,800 / 1.25 = 1,440), Large from 1,656 px
 *  (1,656 / 1.15 = 1,440), Standard below. The width is the window's in CSS px (`innerWidth`), so a browser zoomed to 125% on a 2,000 px
 *  window reads 1,600 and opens at Standard: the console never stacks a size on the browser's zoom by itself. */
export const LARGER_FROM = 1800;
export const LARGE_FROM = 1656;
export function autoTextSize(windowWidth: number): TextSize {
  if (!Number.isFinite(windowWidth)) return 'standard';
  if (windowWidth >= LARGER_FROM) return 'larger';
  if (windowWidth >= LARGE_FROM) return 'large';
  return 'standard';
}

/** A stored value as a step, or null when it is none of the three words (never stored, or something else wrote the key). */
export function textSizeOf(value: string | null | undefined): TextSize | null {
  return value === 'standard' || value === 'large' || value === 'larger' ? value : null;
}

/** THE STEP ON SCREEN: a person's own pick wins; with none, the first-visit default by the window's width. `auto` says which it was (the
 *  hint shows only for a step the console chose). */
export function resolveTextSize(pick: TextSize | null, windowWidth: number): { size: TextSize; auto: boolean } {
  return pick === null ? { size: autoTextSize(windowWidth), auto: true } : { size: pick, auto: false };
}

/** Whether the one-line hint shows: the console chose Large or Larger by itself, and this browser has not seen the hint. */
export function showsTextSizeHint(resolved: { size: TextSize; auto: boolean }, hintSeen: boolean): boolean {
  return resolved.auto && resolved.size !== 'standard' && !hintSeen;
}

/** The hint's words. */
export function textSizeHintWords(size: TextSize): string {
  return `Text size set to ${TEXT_SIZE_WORDS[size]} for your screen; change it here.`;
}

const round2 = (n: number): number => Math.round(n * 100) / 100;

/** ONE WIDTH (OR HEIGHT) THRESHOLD MOVED BY THE FACTOR. A `min-` threshold T becomes T x factor. A `max-` threshold is the last width below a
 *  boundary B (a whole-px `max-width: 1279px` is "below 1,280"; a `1279.98px` one, written to leave no gap under a 1,280 min, is "below
 *  1,280" too), so it becomes B x factor less 0.02: the scaled max and min pairs meet as the originals do (1,471.98 and 1,472 at Large)
 *  with no width that neither or both claim. At factor 1 every threshold is returned unchanged. */
export function scaleThreshold(kind: 'min' | 'max', px: number, factor: number): number {
  if (factor === 1) return px;
  if (kind === 'min') return round2(px * factor);
  const fraction = round2(px - Math.floor(px));
  const boundary = fraction >= 0.5 ? round2(px + (1 - fraction)) : px + 1;
  return round2(boundary * factor - 0.02);
}

const QUERY_PX = /\b(min|max)-(width|height)\s*:\s*(\d+(?:\.\d+)?)px/g;

/** A MEDIA OR CONTAINER QUERY'S px THRESHOLDS MOVED BY THE FACTOR ("(max-width: 1279px)" at 1.15 is "(max-width: 1471.98px)"); every
 *  other word of the query is kept. Width and height alike, since the whole console grows. */
export function scaleQuery(query: string, factor: number): string {
  if (factor === 1) return query;
  return query.replace(QUERY_PX, (_m, kind: 'min' | 'max', axis: string, n: string) => `${kind}-${axis}: ${scaleThreshold(kind, Number(n), factor)}px`);
}

/** Whether a query has a lower bound (`min-width`, `min-height`): such a block must not also apply at a larger step, where its scaled copy
 *  starts later. A query with upper bounds only may (its scaled copy applies wherever it does, with the same declarations). */
export function hasLowerBound(query: string): boolean {
  return /\bmin-(width|height)\s*:/.test(query);
}

/** THE STORE OVER A BROWSER STORAGE (null where there is none): the pick and the hint flag. A choice made in this visit wins over the
 *  storage, so a write the browser refuses still holds until the page reloads; a storage error never throws (the theme's rule, prefs.ts). */
type TextSizeStorage = Pick<Storage, 'getItem' | 'setItem'>;
export interface TextSizeStore {
  pick(): TextSize | null;
  setPick(size: TextSize): void;
  hintSeen(): boolean;
  setHintSeen(): void;
}
export function textSizeStore(storage: TextSizeStorage | null): TextSizeStore {
  let chosen: TextSize | null = null;
  let seen = false;
  const read = (key: string): string | null => {
    try {
      return storage?.getItem(key) ?? null;
    } catch {
      return null;
    }
  };
  const write = (key: string, value: string): void => {
    try {
      storage?.setItem(key, value);
    } catch {
      // The browser refuses storage: the choice lives for this visit only.
    }
  };
  return {
    pick: () => chosen ?? textSizeOf(read(TEXT_SIZE_KEY)),
    setPick(size) {
      chosen = size;
      write(TEXT_SIZE_KEY, size);
    },
    hintSeen: () => seen || read(TEXT_SIZE_HINT_KEY) === 'seen',
    setHintSeen() {
      seen = true;
      write(TEXT_SIZE_HINT_KEY, 'seen');
    },
  };
}
